import { useState } from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  roles: string[];
  orgType: 'internal' | 'county' | 'vendor';
  orgId: string;
  vendor?: string;
  token: string;
  userType?: 'admin' | 'fund_manager' | 'limited_partner' | 'investor';
  preferences?: {
    reportFrequency?: 'daily' | 'weekly' | 'monthly';
    alertTypes?: string[];
    dataAccess?: 'basic' | 'enhanced';
  };
}

const DEMO_USERS: Record<string, User> = {
  'Demo123': {
    id: 'demo-internal',
    name: 'Demo Internal PM',
    email: 'demo123@beehive.com',
    roles: ['projectManager', 'admin'],
    orgType: 'internal',
    orgId: 'beehive-internal',
    token: 'demo-internal-token'
  },
  'DemoCounty': {
    id: 'demo-county',
    name: 'Demo County Admin',
    email: 'democounty@county.gov',
    roles: ['countyAdmin'],
    orgType: 'county',
    orgId: 'demo-county-001',
    token: 'demo-county-token'
  },
  'DemoTyler': {
    id: 'demo-tyler',
    name: 'Demo Tyler PM',
    email: 'demotyler@tylertech.com',
    roles: ['tylerPM'],
    orgType: 'vendor',
    orgId: 'tyler-tech',
    vendor: 'tyler',
    token: 'demo-tyler-token'
  }
};

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('beehive-demo-user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const login = async (username: string, password: string) => {
    if (password !== 'echoecho') {
      throw new Error('Invalid credentials');
    }
    
    const demoUser = DEMO_USERS[username];
    if (!demoUser) {
      throw new Error('User not found');
    }
    
    setUser(demoUser);
    localStorage.setItem('beehive-demo-user', JSON.stringify(demoUser));
    return true;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('beehive-demo-user');
  };

  const hasRole = (role: string): boolean => {
    return user?.roles.includes(role) || false;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => hasRole(role));
  };

  const getVisibleModules = () => {
    if (!user) return [];
    
    const moduleConfig: Record<string, string[]> = {
      'projectManager': ['unified-dashboard', 'app-dock', 'project-workspace', 'administration', 'presentation-studio', 'smart-grid', 'todo-templates', 'airtable-widgets', 'smart-workflow', 'communication', 'meetingscribe', 'annotation', 'pmsensei', 'support-bridge', 'code-ops', 'conversion-ops', 'qa-dashboard', 'swarm-health'],
      'businessAnalyst': ['unified-dashboard', 'app-dock', 'knowledge-files', 'presentation-studio', 'smart-grid', 'todo-templates', 'airtable-widgets', 'agentic-multiview', 'tyler-knowledge', 'meetingscribe', 'annotation', 'analytics', 'support-bridge', 'qa-dashboard', 'swarm-health'],
      'systemArchitect': ['unified-dashboard', 'app-dock', 'operations-control', 'code-ops', 'qa-dashboard', 'smart-grid', 'automation', 'triage', 'tyler-knowledge', 'precision-data', 'implementation-lab', 'support-bridge', 'conversion-ops', 'swarm-health'],
      'admin': ['unified-dashboard', 'app-dock', 'project-workspace', 'administration', 'support-bridge', 'presentation-studio', 'smart-grid', 'automation', 'todo-templates', 'airtable-widgets', 'agentic-multiview', 'smart-workflow', 'adaptive-ux', 'triage', 'tyler-knowledge', 'precision-data', 'communication', 'meetingscribe', 'annotation', 'code-ops', 'conversion-ops', 'qa-dashboard', 'swarm-health'],
      
      'countyAdmin': ['unified-dashboard', 'app-dock', 'communication-center', 'knowledge-files', 'support-bridge', 'presentation-studio', 'smart-grid', 'todo-templates', 'airtable-widgets', 'agentic-multiview', 'adaptive-ux', 'tyler-knowledge', 'qa-dashboard', 'swarm-health'],
      'countyAnalyst': ['unified-dashboard', 'app-dock', 'knowledge-files', 'presentation-studio', 'smart-grid', 'todo-templates', 'airtable-widgets', 'tyler-knowledge', 'meetingscribe', 'annotation', 'analytics', 'support-bridge', 'qa-dashboard'],
      'client': ['unified-dashboard', 'app-dock', 'presentation-studio', 'support-bridge', 'communication', 'meetingscribe', 'knowledge-files'],
      'countyViewer': ['unified-dashboard', 'app-dock', 'support-bridge', 'swarm-health'],
      'viewer': ['unified-dashboard', 'app-dock', 'support-bridge', 'swarm-health'],
      
      'tylerPM': ['unified-dashboard', 'app-dock', 'project-workspace', 'operations-control', 'support-bridge', 'presentation-studio', 'smart-grid', 'automation', 'todo-templates', 'airtable-widgets', 'agentic-multiview', 'smart-workflow', 'adaptive-ux', 'tyler-knowledge', 'precision-data', 'communication', 'meetingscribe', 'annotation', 'code-ops', 'conversion-ops', 'qa-dashboard', 'swarm-health'],
      'tylerEngineer': ['unified-dashboard', 'app-dock', 'operations-control', 'code-ops', 'qa-dashboard', 'smart-grid', 'automation', 'triage', 'tyler-knowledge', 'precision-data', 'implementation-lab', 'support-bridge', 'conversion-ops', 'swarm-health'],
      'tylerExec': ['unified-dashboard', 'app-dock', 'project-workspace', 'support-bridge', 'presentation-studio', 'smart-grid', 'airtable-widgets', 'agentic-multiview', 'adaptive-ux', 'tyler-knowledge', 'qa-dashboard', 'swarm-health']
    };
    
    const allModules = new Set<string>();
    user.roles.forEach(role => {
      const modules = moduleConfig[role] || [];
      modules.forEach(module => allModules.add(module));
    });
    
    return Array.from(allModules);
  };

  const getDataPermissions = () => {
    if (!user) return { canViewEncrypted: false, canExport: false, canPrint: false };
    
    const dataPermissionConfig: Record<string, any> = {
      'admin': { canViewEncrypted: true, canExport: true, canPrint: true },
      'systemArchitect': { canViewEncrypted: true, canExport: true, canPrint: false },
      'tylerEngineer': { canViewEncrypted: true, canExport: false, canPrint: false },
      'projectManager': { canViewEncrypted: false, canExport: true, canPrint: true },
      'businessAnalyst': { canViewEncrypted: false, canExport: false, canPrint: false },
      'countyAdmin': { canViewEncrypted: false, canExport: true, canPrint: true },
      'tylerPM': { canViewEncrypted: false, canExport: true, canPrint: false }
    };
    
    const permissions = { canViewEncrypted: false, canExport: false, canPrint: false };
    
    user.roles.forEach(role => {
      const rolePerms = dataPermissionConfig[role];
      if (rolePerms) {
        permissions.canViewEncrypted = permissions.canViewEncrypted || rolePerms.canViewEncrypted;
        permissions.canExport = permissions.canExport || rolePerms.canExport;
        permissions.canPrint = permissions.canPrint || rolePerms.canPrint;
      }
    });
    
    return permissions;
  };

  const isLimitedPartner = (): boolean => {
    return user?.userType === 'limited_partner' || hasRole('limited_partner') || hasRole('lp');
  };

  const canAccessFullData = (): boolean => {
    return hasAnyRole(['admin', 'fund_manager']) && !isLimitedPartner();
  };


  return {
    user,
    login,
    logout,
    hasRole,
    hasAnyRole,
    getVisibleModules,
    getDataPermissions,
    isLimitedPartner,
    canAccessFullData,
    isAuthenticated: !!user
  };
};
